<template>
  <div class="w-full relative flex flex-col justify-center gap-4">
    <div class="w-full flex flex-row gap-4">
      <!-- Time of Day selector:start -->
      <div :class="{ 'w-1/3': timeOfDay !== 'EXACT_TIME', 'w-1/2': timeOfDay === 'EXACT_TIME' }">
        <label class="form-label">
          {{ $t(`${viewRoot}.form.time_of_day.label`) }}
        </label>

        <TomSelect
          class="w-full cursor"
          v-model="timeOfDay"
          :placeholder="$t(`${viewRoot}.form.time_of_day.placeholder`)"
          :input_hide="true"
          :disabled="readOnly"
        >
          <option v-for="(t, i) in timeOfDayKeys" :key="`tod-${i}`" :value="t">
            {{ $t(`globals.time_of_day.${t}`) }}
          </option>
        </TomSelect>
      </div>
      <!-- Time of Day selector:end -->

      <!-- EXACT_TIME:HH:mm 12h:start -->
      <div v-if="timeOfDay === 'EXACT_TIME'" class="w-1/2">
        <label class="form-label">
          {{ $t(`${viewRoot}.form.time.label`) }}
        </label>

        <input type="time" v-model="timeHoursAndMinutes" class="form-control w-full" :disabled="readOnly" step="60" />
      </div>
      <!-- EXACT_TIME:HH:mm 12h:end -->

      <!-- FIRST/LAST INTAKE, WAKE_UP_TIME, BEDTIME -> minutes +/- -->
      <div v-else-if="offsetModes.includes(timeOfDay)" class="w-2/3 flex flex-row gap-4">
        <!-- Minutes (0..120):start -->
        <div class="w-1/2">
          <label class="form-label">
            {{ $t(`${viewRoot}.form.time.offset_minutes.label`) }}
          </label>

          <input
            type="number"
            v-model.number="offsetMinutes"
            class="form-control w-full"
            :placeholder="$t(`${viewRoot}.form.time.offset_minutes.placeholder`)"
            :disabled="readOnly"
            min="0"
            max="120"
          />
        </div>
        <!-- Minutes (0..120):end -->

        <!-- Relation (BEFORE / AFTER):start -->
        <div class="w-1/2">
          <label class="form-label">
            {{ $t(`${viewRoot}.form.time.offset_relation.label`) }}
          </label>

          <!-- Free Selection:start -->
          <div v-if="!beforeOrWakeUpTimeRelation">
            <TomSelect v-model="offsetRelation" class="w-full cursor" :input_hide="true" :disabled="readOnly">
              <option value="BEFORE">{{ $t(`${viewRoot}.form.time.offset_relation.BEFORE`) }}</option>
              <option value="AFTER">{{ $t(`${viewRoot}.form.time.offset_relation.AFTER`) }}</option>
            </TomSelect>
          </div>
          <!-- Free Selection:end -->

          <!-- Only Read-Only (Blocked):start -->
          <div v-else class="form-control bg-theme-32 cursor-not-allowed select-none">
            {{ $t(`${viewRoot}.form.time.offset_relation.${forcedOffsetRelation}`) }}
          </div>
          <!-- Only Read-Only (Blocked):end -->
        </div>
        <!-- Relation (BEFORE / AFTER):end -->
      </div>
    </div>

    <div v-if="errors.offset_minutes" class="text-theme-6 mt-2">
      {{ errors.offset_minutes }}
    </div>

    <div v-if="errors.time" class="text-theme-6 mt-2">
      {{ errors.time }}
    </div>

    <div class="w-full">{{ helpText }}</div>

    <div v-if="!beforeOrWakeUpTimeRelation" class="w-full">*{{ $t(`${viewRoot}.form.time.note`) }}</div>
  </div>
</template>

<script>
export default {
  name: "TimeOfDayPicker",
  props: {
    modelValue: { type: Object, required: true }, // { time_of_day, time, offset_minutes, offset_relation }
    timeOfDayKeys: { type: Array, required: true },
    viewRoot: { type: String, default: "views.alerts" },
    readOnly: { type: Boolean, default: false },
    errors: {
      type: Object,
      default: () => ({ time_of_day: "", time: "", offset_minutes: "" }),
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      offsetModes: ["FIRST_INTAKE", "LAST_INTAKE", "WAKE_UP_TIME", "BEDTIME"],
    };
  },
  computed: {
    timeOfDay: {
      get() {
        return this.modelValue?.time_of_day ?? "EXACT_TIME";
      },
      set(v) {
        this.mergeAndEmit({ time_of_day: v });
      },
    },
    timeHoursAndMinutes: {
      get() {
        return this.modelValue?.timeHoursAndMinutes ?? "";
      },
      set(v) {
        this.mergeAndEmit({ timeHoursAndMinutes: v });
      },
    },
    offsetMinutes: {
      get() {
        const n = Number(this.modelValue?.offset_minutes);
        return Number.isFinite(n) ? n : 0;
      },
      set(v) {
        const n = Number(v);
        this.mergeAndEmit({ offset_minutes: Number.isFinite(n) ? Math.min(120, Math.max(0, n)) : 0 });
      },
    },
    offsetRelation: {
      get() {
        return this.modelValue?.offset_relation || "AFTER";
      },
      set(v) {
        if (this.beforeOrWakeUpTimeRelation) {
          this.mergeAndEmit({ offset_relation: this.forcedOffsetRelation });
        } else {
          this.mergeAndEmit({ offset_relation: v });
        }
      },
    },
    beforeOrWakeUpTimeRelation() {
      return this.timeOfDay === "BEDTIME" || this.timeOfDay === "WAKE_UP_TIME";
    },
    forcedOffsetRelation() {
      return this.timeOfDay === "BEDTIME" ? "BEFORE" : "AFTER";
    },
    helpText() {
      if (this.timeOfDay === "EXACT_TIME") {
        return this.$t(`${this.viewRoot}.form.time.exact_text`, {
          time: this.getTimeFormatted(this.timeHoursAndMinutes),
        });
      }
      if (this.offsetModes.includes(this.timeOfDay)) {
        const relationText = this.$t(`${this.viewRoot}.form.time.offset_relation.${this.offsetRelation}`).toLowerCase();
        const conditionText = this.$t(`globals.time_of_day.${this.timeOfDay}`).toLowerCase();

        return this.$t(`${this.viewRoot}.form.time.offset_text`, {
          minutes: this.offsetMinutes,
          relation: relationText,
          condition: conditionText,
        });
      }
      return "";
    },
  },
  watch: {
    timeOfDay(mode) {
      if (mode === "EXACT_TIME") {
        this.mergeAndEmit({
          timeHoursAndMinutes: this.timeHoursAndMinutes || "",
          offset_minutes: 0,
          offset_relation: "AFTER",
        });
      } else if (this.offsetModes.includes(mode)) {
        this.mergeAndEmit({
          timeHoursAndMinutes: "",
          offset_minutes: Math.min(120, Math.max(0, this.offsetMinutes || 0)),
          offset_relation: this.beforeOrWakeUpTimeRelation ? this.forcedOffsetRelation : this.offsetRelation || "AFTER",
        });
      }
    },
  },
  methods: {
    mergeAndEmit(patch) {
      this.$emit("update:modelValue", { ...(this.modelValue || {}), ...patch });
    },
    getTimeFormatted(timeStr) {
      let formatted = "--:--";
      if (timeStr) {
        const [h, m] = timeStr.split(":").map(Number);
        if (!isNaN(h) && !isNaN(m)) {
          const hour12 = ((h + 11) % 12) + 1;
          const amOrPm = h < 12 ? "AM" : "PM";
          formatted = `${hour12}:${m.toString().padStart(2, "0")} ${amOrPm}`;
        }
      }
      return formatted;
    },
  },
};
</script>
